/*
 * Design: Swiss Precision × 和の余白
 * Brand: eightone (#E03030 accent, #1A1A1A headings, Noto Sans JP)
 * Section: 支援事例・お客様の声（信頼性向上）
 * Mobile: タブ横スクロール、カード余白最適化、タッチターゲット48px
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Factory, Truck, UtensilsCrossed, ShoppingBag, Quote, TrendingUp, Clock, Users } from "lucide-react";
import { scrollToContactForm } from "@/hooks/useScrollToContact";

interface CaseStudy {
  id: string;
  industry: string;
  icon: typeof Factory;
  company: string;
  companyDesc: string;
  challenge: string;
  solution: string;
  results: { label: string; value: string; icon: typeof TrendingUp }[];
  testimonial: {
    quote: string;
    name: string;
    role: string;
  };
  tags: string[];
}

const CASE_STUDIES: CaseStudy[] = [
  {
    id: "manufacturing",
    industry: "製造業",
    icon: Factory,
    company: "A社（金属加工・従業員80名）",
    companyDesc: "広島県の金属加工メーカー。多品種少量生産で在庫管理と工程管理に課題を抱えていた。",
    challenge: "紙ベースの在庫台帳とExcel管理により、棚卸に毎月2日間を要していた。工程間の情報伝達も口頭・紙ベースで、納期遅延が月5件以上発生。",
    solution: "RFID在庫管理システムとAIエージェントによる工程進捗の自動可視化を実装。Googleスプレッドシートと連携し、リアルタイムで在庫・工程状況をダッシュボード化。",
    results: [
      { label: "棚卸時間", value: "90%削減", icon: Clock },
      { label: "納期遅延", value: "月5件→0件", icon: TrendingUp },
      { label: "在庫精度", value: "99.5%達成", icon: TrendingUp },
    ],
    testimonial: {
      quote: "棚卸が2日から2時間に。現場の『見える化』が進んだことで、作業者自身が改善提案を出すようになりました。",
      name: "製造部長",
      role: "A社",
    },
    tags: ["RFID", "在庫管理", "工程可視化", "Google Workspace"],
  },
  {
    id: "wholesale",
    industry: "卸売・物流業",
    icon: Truck,
    company: "B社（食品卸売・従業員120名）",
    companyDesc: "大阪の食品卸売業者。毎日200件以上の受発注処理を手作業で行い、ミスと残業が常態化。",
    challenge: "FAXとメールで届く注文書を手入力でシステムに転記。入力ミスによるクレームが月10件以上、残業時間は月平均40時間超。",
    solution: "AIエージェントがGmailの受注メールを自動解析し、スプレッドシートに転記。異常値の自動検知と担当者への通知をWorkspace MCP経由で実現。",
    results: [
      { label: "入力ミス", value: "95%削減", icon: TrendingUp },
      { label: "残業時間", value: "月40h→8h", icon: Clock },
      { label: "処理速度", value: "3倍向上", icon: TrendingUp },
    ],
    testimonial: {
      quote: "FAXの山と格闘していた毎日が嘘のよう。AIが下処理してくれるので、私たちは判断業務に集中できるようになりました。",
      name: "業務管理課長",
      role: "B社",
    },
    tags: ["受発注自動化", "Gmail連携", "異常検知", "業務効率化"],
  },
  {
    id: "service",
    industry: "サービス業",
    icon: UtensilsCrossed,
    company: "C社（ホテル・旅館運営・従業員60名）",
    companyDesc: "沖縄のリゾートホテル。予約管理、清掃スケジュール、顧客対応の3つの業務で人手不足が深刻化。",
    challenge: "予約変更のたびに清掃・食事・送迎の3部門に手動で連絡。情報の行き違いによるクレームが月8件、スタッフの疲弊が離職に直結。",
    solution: "Googleカレンダーと連携したAIエージェントが予約変更を自動検知し、関連部門のスケジュールを即時更新。チェックリストの自動生成と進捗管理も実装。",
    results: [
      { label: "連絡ミス", value: "ゼロ達成", icon: TrendingUp },
      { label: "業務時間", value: "60%削減", icon: Clock },
      { label: "顧客満足度", value: "4.2→4.7", icon: Users },
    ],
    testimonial: {
      quote: "予約が変わるたびに走り回っていたのが、今はAIが全部門に自動で伝えてくれる。お客様への対応に集中できるようになりました。",
      name: "支配人",
      role: "C社",
    },
    tags: ["カレンダー連携", "スケジュール自動化", "顧客対応", "チェックリスト"],
  },
  {
    id: "retail",
    industry: "小売・観光業",
    icon: ShoppingBag,
    company: "D社（観光土産品・EC・従業員25名）",
    companyDesc: "広島の観光土産品メーカー。実店舗とECの在庫管理が二重化し、欠品と過剰在庫が頻発。",
    challenge: "実店舗とECの在庫データが別管理で、人気商品の欠品と不人気商品の過剰在庫が常態化。月次の売上分析にも3日間を要していた。",
    solution: "AIエージェントが実店舗POSとEC管理画面のデータをスプレッドシートに自動統合。需要予測と発注提案を毎朝自動生成し、Gmailで担当者に配信。",
    results: [
      { label: "欠品率", value: "80%改善", icon: TrendingUp },
      { label: "過剰在庫", value: "40%削減", icon: TrendingUp },
      { label: "分析時間", value: "3日→30分", icon: Clock },
    ],
    testimonial: {
      quote: "毎朝メールで届く発注提案のおかげで、勘に頼っていた仕入れが数字に基づく判断に変わりました。売上も15%伸びています。",
      name: "代表取締役",
      role: "D社",
    },
    tags: ["在庫統合", "需要予測", "EC連携", "売上分析"],
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export function CaseStudySection() {
  const [activeCase, setActiveCase] = useState("manufacturing");
  const currentCase = CASE_STUDIES.find((c) => c.id === activeCase)!;

  return (
    <section id="cases" className="py-14 sm:py-20 lg:py-28 bg-[#F9F9F9]">
      <div className="container">
        {/* Section Header */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-12">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span
              className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Case Studies
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            支援事例・お客様の声
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-2xl leading-relaxed">
            業種も規模もさまざまな企業で、AIエージェントによる業務改善が成果を上げています。
          </p>
        </motion.div>

        {/* Industry Tabs - Horizontal scroll on mobile */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.1 }} className="mb-6 sm:mb-10">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-hide">
            {CASE_STUDIES.map((cs) => (
              <button
                key={cs.id}
                onClick={() => setActiveCase(cs.id)}
                className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-medium border transition-all duration-200 rounded-sm shrink-0 touch-manipulation ${
                  activeCase === cs.id
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    : "bg-white text-[#333333] border-[#E0E0E0] hover:border-[#E03030] hover:text-[#E03030]"
                }`}
              >
                <cs.icon size={16} strokeWidth={1.5} />
                {cs.industry}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Case Study Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentCase.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4 }}
          >
            {/* Main Card */}
            <div className="border border-[#E0E0E0] bg-white">
              {/* Top: Company Info */}
              <div className="p-4 sm:p-6 lg:p-8 border-b border-[#E0E0E0]">
                <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 mb-2 sm:mb-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-[#1A1A1A] text-white rounded-sm">
                    <currentCase.icon size={18} strokeWidth={1.5} className="sm:hidden" />
                    <currentCase.icon size={20} strokeWidth={1.5} className="hidden sm:block" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A]">{currentCase.company}</h3>
                    <p className="text-[10px] sm:text-xs text-[#666666]">{currentCase.companyDesc}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                  {currentCase.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 sm:px-2.5 py-0.5 sm:py-1 text-[9px] sm:text-[10px] font-medium tracking-wider text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Middle: Challenge → Solution → Results */}
              <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-[#E0E0E0]">
                {/* Challenge */}
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-[#E03030]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Challenge
                    </span>
                  </div>
                  <h4 className="text-xs sm:text-sm font-bold text-[#1A1A1A] mb-1.5 sm:mb-2">ご支援前の課題</h4>
                  <p className="text-xs sm:text-sm text-[#333333] leading-relaxed">{currentCase.challenge}</p>
                </div>

                {/* Solution */}
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-[#1A1A1A]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Solution
                    </span>
                  </div>
                  <h4 className="text-xs sm:text-sm font-bold text-[#1A1A1A] mb-1.5 sm:mb-2">実施したソリューション</h4>
                  <p className="text-xs sm:text-sm text-[#333333] leading-relaxed">{currentCase.solution}</p>
                </div>

                {/* Results */}
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-[#E03030]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Results
                    </span>
                  </div>
                  <h4 className="text-xs sm:text-sm font-bold text-[#1A1A1A] mb-3 sm:mb-4">改善効果</h4>
                  <div className="space-y-3 sm:space-y-4">
                    {currentCase.results.map((result) => (
                      <div key={result.label} className="flex items-center gap-2.5 sm:gap-3">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-[#FEF2F2] text-[#E03030] rounded-sm shrink-0">
                          <result.icon size={14} strokeWidth={1.5} className="sm:hidden" />
                          <result.icon size={16} strokeWidth={1.5} className="hidden sm:block" />
                        </div>
                        <div>
                          <div className="text-[10px] sm:text-xs text-[#666666]">{result.label}</div>
                          <div className="text-sm sm:text-base font-extrabold text-[#E03030]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {result.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom: Testimonial */}
              <div className="p-4 sm:p-6 lg:p-8 border-t border-[#E0E0E0] bg-white">
                <div className="flex gap-3 sm:gap-4">
                  <Quote size={20} className="shrink-0 text-[#E03030] opacity-40 mt-0.5 sm:hidden" />
                  <Quote size={24} className="shrink-0 text-[#E03030] opacity-40 mt-1 hidden sm:block" />
                  <div>
                    <p className="text-xs sm:text-sm lg:text-base text-[#333333] leading-relaxed italic mb-3 sm:mb-4">
                      {currentCase.testimonial.quote}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-[#F9F9F9] border border-[#E0E0E0] rounded-full">
                        <Users size={12} className="text-[#666666] sm:hidden" />
                        <Users size={14} className="text-[#666666] hidden sm:block" />
                      </div>
                      <div>
                        <span className="text-xs sm:text-sm font-bold text-[#1A1A1A]">{currentCase.testimonial.name}</span>
                        <span className="text-[10px] sm:text-xs text-[#666666] ml-2">{currentCase.testimonial.role}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA after case study */}
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-4 sm:p-6 bg-[#F9F9F9] border border-[#E0E0E0]">
              <div>
                <p className="text-sm font-bold text-[#1A1A1A]">
                  同じ業種の課題をお持ちですか？
                </p>
                <p className="text-[10px] sm:text-xs text-[#666666] mt-0.5 sm:mt-1">
                  御社の状況に合わせた具体的な改善プランをご提案します。
                </p>
              </div>
              <button
                onClick={() => scrollToContactForm()}
                className="shrink-0 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:py-3 bg-[#E03030] text-white font-bold text-sm rounded hover:bg-[#c72828] transition-colors duration-200 active:scale-[0.98] touch-manipulation"
              >
                無料相談を予約する
                <ArrowRight size={14} />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
